import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import {
  Box, Typography, Card, CardContent, CircularProgress,
  useTheme, useMediaQuery, TextField, InputAdornment, IconButton,
  Tooltip, Alert
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { format } from 'date-fns';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';

interface SaleRow {
  id: string;
  customer_name: string;
  total_amount: number;
  discount: number;
  notes: string;
  created_at: string;
  date: string;
}

export function PastSales(): JSX.Element {
  const { user } = useAuth();
  const [sales, setSales] = useState<SaleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (user) {
      fetchSales();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchSales = async () => {
    try {
      setLoading(true);
      setError(null);
      if (!user) return;
      
      const { data, error } = await supabase
        .from('sales')
        .select('*')
        .eq('employee_id', user.id);
        
      if (error) throw error;

      const formattedSales = (data || []).map(sale => ({
        ...sale,
        date: format(new Date(sale.created_at), 'MM/dd/yyyy')
      }));
      setSales(formattedSales);
    } catch (error) {
      setError('An unexpected error occurred');
      console.error('Error fetching sales:', error);
    } finally {
      setLoading(false);
    }
  };

  const uniqueClients = useMemo(() => {
    return Array.from(new Set(sales.map(s => s.customer_name))).sort();
  }, [sales]);

  const filteredSales = useMemo(() => {
    return sales.filter(sale => {
      const matchesSearch = sale.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            sale.notes?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesClient = clientFilter ? sale.customer_name === clientFilter : true;
      const matchesMinAmt = minAmount ? Number(sale.total_amount) >= Number(minAmount) : true;
      const matchesMaxAmt = maxAmount ? Number(sale.total_amount) <= Number(maxAmount) : true;
      
      let matchesStart = true;
      let matchesEnd = true;
      if (startDate) {
        matchesStart = new Date(sale.created_at) >= new Date(startDate);
      }
      if (endDate) {
        const end = new Date(endDate);
        end.setDate(end.getDate() + 1);
        matchesEnd = new Date(sale.created_at) < end;
      }
      return matchesSearch && matchesClient && matchesMinAmt && matchesMaxAmt && matchesStart && matchesEnd;
    });
  }, [sales, searchTerm, clientFilter, minAmount, maxAmount, startDate, endDate]);

  const columns: GridColDef[] = [
    { field: 'customer_name', headerName: 'Customer', flex: 1, minWidth: 150 },
    { field: 'date', headerName: 'Date', minWidth: 120 },
    { 
      field: 'total_amount', 
      headerName: 'Amount', 
      minWidth: 120,
      valueFormatter: (params) => `₹${Number(params?.value || 0).toFixed(2)}`
    },
    { 
      field: 'discount', 
      headerName: 'Discount', 
      minWidth: 100,
      valueFormatter: (params) => `₹${Number(params?.value || 0).toFixed(2)}`
    },
    { field: 'notes', headerName: 'Notes', flex: 1, minWidth: 150 }
  ];

  return (
    <Box sx={{ mt: 3 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6">Past Sales</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                placeholder="Search..."
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                }}
              />
              <Tooltip title="Refresh">
                <IconButton onClick={fetchSales} color="primary">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              select
              label="Client"
              size="small"
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              sx={{ minWidth: 150 }}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
            >
              <option value="">All Clients</option>
              {uniqueClients.map(c => <option key={c} value={c}>{c}</option>)}
            </TextField>
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField type="number" size="small" label="Min ₹" sx={{ width: 80 }} value={minAmount} onChange={e => setMinAmount(e.target.value)} />
            <TextField type="number" size="small" label="Max ₹" sx={{ width: 80 }} value={maxAmount} onChange={e => setMaxAmount(e.target.value)} />
          </Box>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={filteredSales}
              columns={columns}
              getRowId={(row) => row.id}
              autoHeight
              density="compact"
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
              }}
              pageSizeOptions={[5, 10, 25]}
              disableColumnMenu={isMobile}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
